import { FieldProvenanceDto, MissionAssessmentFieldName } from '@@types/api.types';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { FormSuggestion, useFormSuggestion } from '@components/shared/form-suggestion.component';
import { withForm } from '@components/ui/form/form';
import { config } from '@config';
import maDefinitions from '@data/ma-definitions.json';
import { useScrollToField } from '@hooks/use-scroll-to-field.hook';
import { missionAssessmentHurdlesSchema } from '@schemas/mission-assessment.schema';
import { MAX_MRL_SCORE, MAX_TRL_SCORE, MRL_TRL_SETTINGS } from '@utils/constants';
import { createSuggestionTextApplier } from '@utils/form.util';
import type { ProvenanceAcceptEntry } from '@utils/mission-assessment-provenance.util';
import { useMemo, useRef } from 'react';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';
import { MissionAssessmentGenerateButton } from './mission-assessment-generate-button.component';

const isAIEnabled = config.ai.enabled;

export const MissionAssessmentFormHurdles = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    currentMrlScore: undefined as number | undefined,
    currentTrlScore: undefined as number | undefined,
    onProvenanceAccept: undefined as ((entries: ProvenanceAcceptEntry[]) => void) | undefined,
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
    technologyId: undefined as string | undefined,
  },
  render: function Render({ form, currentMrlScore, currentTrlScore, onProvenanceAccept, provenance, technologyId }) {
    const nextTrlRef = useRef<HTMLLabelElement>(null);
    const nextMrlRef = useRef<HTMLLabelElement>(null);
    const supplyChainRef = useRef<HTMLLabelElement>(null);

    const scrollToField = useScrollToField<MissionAssessmentFieldName>({
      [MissionAssessmentFieldName.NEXT_TRL]: nextTrlRef,
      [MissionAssessmentFieldName.NEXT_MRL]: nextMrlRef,
      [MissionAssessmentFieldName.SUPPLY_CHAIN]: supplyChainRef,
    });

    const {
      handleSuggestionStream,
      acceptSuggestion,
      rejectSuggestion,
      getSuggestion,
      getSuggestionModel,
      getIsStreaming,
      handleMetadata,
      handleStreamingStart,
      handleStreamingEnd,
    } = useFormSuggestion<MissionAssessmentFieldName>(scrollToField);

    const applySuggestionText = createSuggestionTextApplier<MissionAssessmentFieldName>({
      [MissionAssessmentFieldName.NEXT_MRL]: (text) => form.setFieldValue('data.hurdles.nextMrl', text),
      [MissionAssessmentFieldName.NEXT_TRL]: (text) => form.setFieldValue('data.hurdles.nextTrl', text),
      [MissionAssessmentFieldName.SUPPLY_CHAIN]: (text) => form.setFieldValue('data.hurdles.supplyChain', text),
    });

    const handleAcceptSuggestion = (fieldName: MissionAssessmentFieldName) => {
      acceptSuggestion(fieldName, (text, meta) => {
        applySuggestionText(fieldName, typeof text === 'string' ? text : '');
        onProvenanceAccept?.([{ key: fieldName, llmLogId: meta?.llmLogId }]);
      });
    };

    const trlScoreOptions = useMemo(() => {
      const options: number[] = [];
      for (let i = 1; i <= MAX_TRL_SCORE; i++) {
        options.push(i);
      }
      return options;
    }, []);

    const mrlScoreOptions = useMemo(() => {
      const options: number[] = [];
      for (let i = 1; i <= MAX_MRL_SCORE; i++) {
        options.push(i);
      }
      return options;
    }, []);

    const isTrlAtMaximum = currentTrlScore === MAX_TRL_SCORE;
    const isMrlAtMaximum = currentMrlScore === MAX_MRL_SCORE;

    return (
      <div className="space-y-4">
        <div className="space-y-4">
          <p className="text-lg text-gray-700">Hurdles</p>
          <p className="text-base text-gray-700">Hurdles to Next TRL</p>

          <div className="pl-4 space-y-4">
            <form.AppField
              name="data.hurdles.nextTrlScore"
              validators={{
                onChange: missionAssessmentHurdlesSchema.shape.nextTrlScore,
              }}
            >
              {(field) => (
                <field.SelectField
                  label="Next TRL Score"
                  placeholder={isTrlAtMaximum ? 'The score is already at its maximum value' : 'Select TRL score'}
                  options={trlScoreOptions.map((score) => ({
                    label: `TRL ${score} - ${
                      MRL_TRL_SETTINGS.trl.levels.find((level) => level.level === score)?.label || `TRL ${score}`
                    }`,
                    value: score.toString(),
                  }))}
                  disabled={isTrlAtMaximum}
                />
              )}
            </form.AppField>

            <form.AppField
              name="data.hurdles.nextTrl"
              validators={{
                onBlur: missionAssessmentHurdlesSchema.shape.nextTrl,
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <div className="flex gap-1.5 items-center">
                    <label
                      className="text-sm font-medium text-gray-700 w-auto"
                      ref={nextTrlRef}
                    >
                      Notes
                    </label>
                    <FieldProvenanceBadge
                      context="form"
                      provenance={provenance?.[MissionAssessmentFieldName.NEXT_TRL]}
                    />
                  </div>
                  <field.TextAreaField
                    disabled={isTrlAtMaximum}
                    generateButton={
                      technologyId &&
                      isAIEnabled && (
                        <MissionAssessmentGenerateButton
                          disabled={isTrlAtMaximum}
                          fieldName={MissionAssessmentFieldName.NEXT_TRL}
                          onAccept={(text) => applySuggestionText(MissionAssessmentFieldName.NEXT_TRL, text)}
                          onStream={(text) =>
                            handleSuggestionStream({ key: MissionAssessmentFieldName.NEXT_TRL, text })
                          }
                          onMetadata={(metadata) =>
                            handleMetadata({ key: MissionAssessmentFieldName.NEXT_TRL, metadata })
                          }
                          onStreamingStart={() => handleStreamingStart(MissionAssessmentFieldName.NEXT_TRL)}
                          onStreamingEnd={() => handleStreamingEnd(MissionAssessmentFieldName.NEXT_TRL)}
                          technologyId={technologyId}
                        />
                      )
                    }
                    placeholder={
                      isTrlAtMaximum
                        ? 'The score is already at its maximum value'
                        : "What items can be improved and where's the threshold"
                    }
                    rows={4}
                    tooltip={maDefinitions?.timeToReplace}
                  />

                  <FormSuggestion
                    suggestion={getSuggestion(MissionAssessmentFieldName.NEXT_TRL)}
                    onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.NEXT_TRL)}
                    onReject={() => rejectSuggestion(MissionAssessmentFieldName.NEXT_TRL)}
                    rejectButtonProps={{ variant: 'destructive' }}
                    acceptButtonProps={{ disabled: isTrlAtMaximum }}
                    model={getSuggestionModel(MissionAssessmentFieldName.NEXT_TRL)}
                    isStreaming={getIsStreaming(MissionAssessmentFieldName.NEXT_TRL)}
                  />
                </div>
              )}
            </form.AppField>
          </div>
        </div>

        {/* Hurdles to Next MRL Section */}
        <div className="space-y-4">
          <p className="text-base text-gray-700">Hurdles to Next MRL</p>

          <div className="pl-4 space-y-4">
            <form.AppField
              name="data.hurdles.nextMrlScore"
              validators={{
                onChange: missionAssessmentHurdlesSchema.shape.nextMrlScore,
              }}
            >
              {(field) => (
                <field.SelectField
                  label="Next MRL Score"
                  placeholder={isMrlAtMaximum ? 'The score is already at its maximum value' : 'Select MRL score'}
                  options={mrlScoreOptions.map((score) => ({
                    label: `MRL ${score} - ${
                      MRL_TRL_SETTINGS.mrl.levels.find((level) => level.level === score)?.label || `MRL ${score}`
                    }`,
                    value: score.toString(),
                  }))}
                  disabled={isMrlAtMaximum}
                />
              )}
            </form.AppField>

            <form.AppField
              name="data.hurdles.nextMrl"
              validators={{
                onBlur: missionAssessmentHurdlesSchema.shape.nextMrl,
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <div className="flex gap-1.5 items-center">
                    <label
                      className="text-sm font-medium text-gray-700 w-auto"
                      ref={nextMrlRef}
                    >
                      Notes
                    </label>
                    <FieldProvenanceBadge
                      context="form"
                      provenance={provenance?.[MissionAssessmentFieldName.NEXT_MRL]}
                    />
                  </div>
                  <field.TextAreaField
                    placeholder={
                      isMrlAtMaximum
                        ? 'The score is already at its maximum value'
                        : "What items can be improved and where's the threshold"
                    }
                    rows={3}
                    disabled={isMrlAtMaximum}
                    tooltip={maDefinitions?.timeToReplace}
                    generateButton={
                      technologyId &&
                      isAIEnabled && (
                        <MissionAssessmentGenerateButton
                          disabled={isMrlAtMaximum}
                          fieldName={MissionAssessmentFieldName.NEXT_MRL}
                          onAccept={(text) => applySuggestionText(MissionAssessmentFieldName.NEXT_MRL, text)}
                          onMetadata={(metadata) =>
                            handleMetadata({ key: MissionAssessmentFieldName.NEXT_MRL, metadata })
                          }
                          onStream={(text) =>
                            handleSuggestionStream({ key: MissionAssessmentFieldName.NEXT_MRL, text })
                          }
                          onStreamingStart={() => handleStreamingStart(MissionAssessmentFieldName.NEXT_MRL)}
                          onStreamingEnd={() => handleStreamingEnd(MissionAssessmentFieldName.NEXT_MRL)}
                          technologyId={technologyId}
                        />
                      )
                    }
                  />

                  <FormSuggestion
                    suggestion={getSuggestion(MissionAssessmentFieldName.NEXT_MRL)}
                    onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.NEXT_MRL)}
                    onReject={() => rejectSuggestion(MissionAssessmentFieldName.NEXT_MRL)}
                    rejectButtonProps={{ variant: 'destructive' }}
                    acceptButtonProps={{ disabled: isMrlAtMaximum }}
                    model={getSuggestionModel(MissionAssessmentFieldName.NEXT_MRL)}
                    isStreaming={getIsStreaming(MissionAssessmentFieldName.NEXT_MRL)}
                  />
                </div>
              )}
            </form.AppField>
          </div>
        </div>

        {/* Hurdles to Supply Chain Section */}
        <form.AppField
          name="data.hurdles.supplyChain"
          validators={{
            onBlur: missionAssessmentHurdlesSchema.shape.supplyChain,
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <div className="flex gap-1.5 items-center">
                <label
                  className="text-sm font-medium text-gray-700 w-auto"
                  ref={supplyChainRef}
                >
                  Hurdles to Supply Chain
                </label>
                <FieldProvenanceBadge
                  context="form"
                  provenance={provenance?.[MissionAssessmentFieldName.SUPPLY_CHAIN]}
                />
              </div>

              <field.TextAreaField
                generateButton={
                  technologyId &&
                  isAIEnabled && (
                    <MissionAssessmentGenerateButton
                      fieldName={MissionAssessmentFieldName.SUPPLY_CHAIN}
                      onAccept={(text) => applySuggestionText(MissionAssessmentFieldName.SUPPLY_CHAIN, text)}
                      onStream={(text) =>
                        handleSuggestionStream({ key: MissionAssessmentFieldName.SUPPLY_CHAIN, text })
                      }
                      onMetadata={(metadata) =>
                        handleMetadata({ key: MissionAssessmentFieldName.SUPPLY_CHAIN, metadata })
                      }
                      onStreamingStart={() => handleStreamingStart(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                      onStreamingEnd={() => handleStreamingEnd(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                      technologyId={technologyId}
                    />
                  )
                }
                placeholder="What items can be improved and where's the threshold"
                rows={3}
                tooltip={maDefinitions?.costToReplace}
              />

              <FormSuggestion
                suggestion={getSuggestion(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                onAccept={() => handleAcceptSuggestion(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                onReject={() => rejectSuggestion(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                rejectButtonProps={{ variant: 'destructive' }}
                model={getSuggestionModel(MissionAssessmentFieldName.SUPPLY_CHAIN)}
                isStreaming={getIsStreaming(MissionAssessmentFieldName.SUPPLY_CHAIN)}
              />
            </div>
          )}
        </form.AppField>
      </div>
    );
  },
});
